import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';

import ChooseLevelDropdown from '@/features/Shipping/components/AssignProductsTable/components/ChooseLevelDropdown';
import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import TableInstance from '@/shared/constants/tableInstance';
import ProductVariant from '@/shared/types/products/productVariant.type';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';

import useAssignedProductsAvdVariantsTable from './hooks/useAssignedProductsAvdVariantsTable';

type AssignedProductsAvdVariantsTableProps = {
  shippingProfile: ShippingProfile;
  onAssignVariants: (assignedVariants: ProductVariant[]) => void;
  onRemoveAssignedVariants: (variants: ProductVariant[]) => void;
};

const AssignedProductsAndVariantsTable: FC<
  AssignedProductsAvdVariantsTableProps
> = ({ shippingProfile, onAssignVariants, onRemoveAssignedVariants }) => {
  const { t } = useTranslation(['shipping', 'common']);

  const { model, table, level, apiRef, tableColumnsState, setLevel } =
    useAssignedProductsAvdVariantsTable({
      shippingProfile,
    });

  return (
    <div style={{ height: '60vh', width: '100%' }}>
      <DataGridTable
        getRowId={(row) => row.variant_id as string}
        rows={table.data}
        rowCount={table.totalRows}
        loading={table.isLoading}
        columns={tableColumnsState.columns}
        columnVisibilityModel={tableColumnsState.visibilityModel}
        onColumnVisibilityModelChange={tableColumnsState.setVisibilityModel}
        customProps={{
          model,
          apiRef,
          tableInstance: TableInstance.AssignedProducts,
          toolbar: {
            title: t('shipping:assignedProducts'),
            toolbarActions: (
              <Button
                variant='contained'
                onClick={() => onAssignVariants(table.data)}
              >
                {t('shipping:assignProducts')}
              </Button>
            ),
            toolbarFilters: (
              <ChooseLevelDropdown level={level} setLevel={setLevel} />
            ),
            rowActions: [
              {
                type: 'default',
                button: {
                  label: t('common:remove'),
                  onClick: (apiRef, rowId) => {
                    const selectedRow = apiRef.current.getRow(
                      rowId || '',
                    ) as ProductVariant;
                    onRemoveAssignedVariants([selectedRow]);
                  },
                },
              },
            ],
          },
          snackBarProps: {
            actions: [
              {
                type: 'default',
                button: {
                  label: t('common:remove'),
                  color: 'error',
                  onClick: (apiRef) => {
                    const selectedRows = [
                      ...apiRef.current.getSelectedRows().values(),
                    ] as ProductVariant[];
                    onRemoveAssignedVariants(selectedRows);
                  },
                },
              },
            ],
          },
        }}
      />
    </div>
  );
};

export default AssignedProductsAndVariantsTable;
